import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    target?.addEventListener(
      'change',
      this.#handleInstantVariantMedia,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Instant switch for Color / Colour image variants.
   *
   * Runs before Shopify's section fetch finishes.
   *
   * @param {Event} event
   */
  #handleInstantVariantMedia = (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    if (input.dataset.instantMedia !== 'true') return;

    const mediaId = input.dataset.optionMediaId;

    if (!mediaId) return;

    this.selectMediaById(mediaId);
  };

  /**
   * Find the already-rendered Shopify media in the current
   * gallery and immediately select its slideshow slide.
   *
   * @param {string | number} mediaId
   * @returns {boolean}
   */
  selectMediaById(mediaId) {
    const requestedId = String(mediaId);

    /*
     * Horizon places the actual Shopify media ID on:
     *
     * .product-media[data-media-id]
     */
    const productMediaElements = Array.from(
      this.querySelectorAll('.product-media[data-media-id]')
    );

    const productMedia = productMediaElements.find(
      (element) => element.dataset.mediaId === requestedId
    );

    if (!productMedia) {
      return false;
    }

    /*
     * CAROUSEL / SLIDESHOW PRESENTATION
     *
     * Find the slideshow-slide containing that product-media.
     */
    const targetSlide = productMedia.closest('slideshow-slide');

    if (targetSlide && this.slideshow) {
      const slides = Array.from(
        this.slideshow.querySelectorAll('slideshow-slide')
      );

      const slideIndex = slides.indexOf(targetSlide);

      if (slideIndex >= 0) {
        this.slideshow.select(
          slideIndex,
          undefined,
          { animate: false }
        );

        return true;
      }
    }

    /*
     * GRID PRESENTATION
     *
     * Grid gallery items use ref="media[]" rather than
     * slideshow-slide.
     */
    const mediaItems = Array.from(this.media || []);

    const gridIndex = mediaItems.findIndex(
      (mediaItem) =>
        mediaItem === productMedia ||
        mediaItem.contains(productMedia)
    );

    if (gridIndex >= 0) {
      mediaItems[gridIndex].scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      });

      return true;
    }

    /*
     * Last safe fallback.
     */
    productMedia.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'nearest',
    });

    return true;
  }

  /**
   * Shopify's normal product-selection response.
   *
   * For our image-color picker, we DO NOT need to replace
   * the entire gallery again after the server request.
   *
   * The image was already selected instantly.
   *
   * Other variant types continue using Shopify's normal
   * gallery replacement behavior.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const picker = event.target;

    /*
     * The ProductSelectEvent is fired by <variant-picker>.
     *
     * Its public selectedOption getter gives us the currently
     * selected real Shopify radio.
     */
    const selectedOption = picker.selectedOption;

    const isInstantColorChange =
      selectedOption instanceof HTMLInputElement &&
      selectedOption.dataset.instantMedia === 'true';

    const isConnectedProduct =
      Boolean(event.detail?.connectedProductUrl);

    /*
     * Ordinary color change:
     *
     * do NOT wait 2-3 seconds and replace the gallery.
     *
     * Price, availability, ATC and the other Shopify listeners
     * still receive ProductSelectEvent normally.
     */
    if (isInstantColorChange && !isConnectedProduct) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const newMediaGallery =
          html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Handles zoom media selection.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  /**
   * Opens zoom.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}